import { useState, useEffect } from "react";
import api from "../services/api";
import "./UserForm.css";

const UserForm = ({ user, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "user",
    plan: "free",
    extractionCredits: 1,
    freeTrialUsed: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        password: "",
        role: user.role || "user",
        plan: user.subscription?.plan || "free",
        extractionCredits: user.subscription?.extractionCredits || 1,
        freeTrialUsed: user.freeTrialUsed || false,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (user) {
        // Update existing user
        const updateData = {
          email: formData.email,
          role: formData.role,
          subscription: {
            plan: formData.plan,
            extractionCredits: parseInt(formData.extractionCredits),
          },
          freeTrialUsed: formData.freeTrialUsed,
        };
        await api.put(`/admin/users/${user._id}`, updateData);
      } else {
        // Create new user
        if (!formData.password || formData.password.length < 6) {
          setError("Password must be at least 6 characters long");
          setLoading(false);
          return;
        }

        const createData = {
          email: formData.email,
          password: formData.password,
          role: formData.role,
          subscription: {
            plan: formData.plan,
            extractionCredits: parseInt(formData.extractionCredits),
          },
        };
        await api.post("/admin/users", createData);
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save user");
      console.error("Error saving user:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-uf-modal-overlay" onClick={onCancel}>
      <div
        className="admin-uf-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-uf-modal-header">
          <h3 className="admin-uf-modal-title">
            <i className={`fa ${user ? "fa-pencil-square-o" : "fa-user-plus"} me-2`}></i>
            {user ? "Edit User" : "Create New User"}
          </h3>
          <button className="admin-uf-close-btn" onClick={onCancel}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        {error && (
          <div className="alert alert-danger">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="admin-uf-modal-body">
            <div className="admin-uf-section">
              <h5 className="admin-uf-section-title">
                <i className="fa fa-id-card-o me-2"></i>
                Account Information
              </h5>

              <div className="admin-uf-form-group">
                <label className="admin-uf-label">
                  Email Address <span className="admin-uf-required">*</span>
                </label>
                <div className="admin-uf-input-wrapper">
                  <i className="fa fa-envelope admin-uf-input-icon"></i>
                  <input
                    type="email"
                    name="email"
                    className="admin-uf-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="user@example.com"
                  />
                </div>
              </div>

              {!user && (
                <div className="admin-uf-form-group">
                  <label className="admin-uf-label">
                    Password <span className="admin-uf-required">*</span>
                  </label>
                  <div className="admin-uf-input-wrapper">
                    <i className="fa fa-lock admin-uf-input-icon"></i>
                    <input
                      type="password"
                      name="password"
                      className="admin-uf-input"
                      value={formData.password}
                      onChange={handleChange}
                      required={!user}
                      minLength="6"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                  <small className="admin-uf-help-text">
                    Must be at least 6 characters long
                  </small>
                </div>
              )}

              <div className="admin-uf-form-group">
                <label className="admin-uf-label">
                  Role <span className="admin-uf-required">*</span>
                </label>
                <div className="admin-uf-input-wrapper">
                  <i className="fa fa-shield admin-uf-input-icon"></i>
                  <select
                    name="role"
                    className="admin-uf-select"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="admin-uf-divider"></div>

            <div className="admin-uf-section">
              <h5 className="admin-uf-section-title">
                <i className="fa fa-credit-card me-2"></i>
                Subscription Details
              </h5>

              <div className="admin-uf-form-group">
                <label className="admin-uf-label">
                  Subscription Plan <span className="admin-uf-required">*</span>
                </label>
                <div className="admin-uf-input-wrapper">
                  <i className="fa fa-star admin-uf-input-icon"></i>
                  <select
                    name="plan"
                    className="admin-uf-select"
                    value={formData.plan}
                    onChange={handleChange}
                    required
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
              </div>

              <div className="admin-uf-form-group">
                <label className="admin-uf-label">
                  Extraction Credits <span className="admin-uf-required">*</span>
                </label>
                <div className="admin-uf-input-wrapper">
                  <i className="fa fa-database admin-uf-input-icon"></i>
                  <input
                    type="number"
                    name="extractionCredits"
                    className="admin-uf-input"
                    value={formData.extractionCredits}
                    onChange={handleChange}
                    required
                    min="0"
                    placeholder="Enter credit amount"
                  />
                </div>
              </div>

              {user && (
                <div className="admin-uf-form-group">
                  <div className="admin-uf-checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="freeTrialUsed"
                      id="freeTrialUsed"
                      className="admin-uf-checkbox"
                      checked={formData.freeTrialUsed}
                      onChange={handleChange}
                    />
                    <label htmlFor="freeTrialUsed" className="admin-uf-checkbox-label">
                      <i className="fa fa-hourglass-half me-2"></i>
                      Free trial has been used
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="admin-uf-modal-footer">
            <button
              type="button"
              className="admin-uf-btn-cancel"
              onClick={onCancel}
              disabled={loading}
            >
              <i className="fa fa-times-circle me-2"></i>
              Cancel
            </button>
            <button
              type="submit"
              className="admin-uf-btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-check-circle me-2"></i>
                  {user ? "Update User" : "Create User"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;
